import React from 'react';

import PropTypes from 'prop-types';

import {
  VALID_DATE_FORMATS,
  stylePropType,
  useForwardedRef,
  StylableComponent,
} from '@adsk/alloy-react-helpers';
import Overlay from '@adsk/alloy-react-overlay';
import Portal from '@adsk/alloy-react-portal';
import theme from '@adsk/alloy-react-theme';

import { DATE_PICKER_VARIANTS } from './consts';
import useDatePicker from './hooks/useDatePicker';
import Footer from './layout/Footer';
import DatePickerOverlay from './layout/Overlay';
import Trigger from './layout/Trigger';
import { DatePickerArgs } from './types';

type DatePickerProps = DatePickerArgs &
  Omit<StylableComponent<HTMLDivElement>, 'onChange'>;
/**
 * The Date Picker is a basic component that allows the user to pick a date or date range
 * Date pickers allow the selection of specific dates and years. It displays a default text field and a dropdown calendar appears when a user taps on the text field.
 */
const DatePicker = React.forwardRef<HTMLDivElement, DatePickerProps>(
  (
    {
      style,
      className,
      variant = DATE_PICKER_VARIANTS.FIELD,
      container = document.body,
      // WPLT-1319
      // eslint-disable-next-line @adsk/react-render-props/render-props-as-components
      renderTrigger: DatePickerTrigger = Trigger,
      overlayProps = {},
      ...props
    },
    ref
  ) => {
    const target = useForwardedRef(ref);
    const datePickerData = useDatePicker({
      variant,
      target,
      ...props,
    });
    const {
      open,
      targetProps,
      placement,
      getTriggerProps,
      arrowProps,
      overlayContainerProps,
    } = datePickerData;

    return (
      <>
        <div
          className={className}
          css={[
            {
              display: 'block',
            },
            style,
          ]}
          {...targetProps}
        >
          <DatePickerTrigger {...getTriggerProps()} />
        </div>
        {open && (
          <Portal container={container}>
            <Overlay.Container placement={placement} {...overlayContainerProps}>
              {variant === DATE_PICKER_VARIANTS.ICON && (
                <Overlay.Arrow
                  placement={placement}
                  color={theme.colors.white}
                  {...arrowProps}
                />
              )}
              <DatePickerOverlay
                range={props.range}
                double={props.double}
                calendarProps={props.calendarProps}
                onMonthChange={props.onMonthChange}
                showOutsideDays={props.showOutsideDays}
                isDateSelectable={props.isDateSelectable}
                yearsToDisplay={props.yearsToDisplay}
                firstDayOfWeek={props.firstDayOfWeek}
                tooltipContentForDate={props.tooltipContentForDate}
                hasEventForDate={props.hasEventForDate}
                renderFooter={props.renderFooter}
                renderFooterActions={props.renderFooterActions}
                minDate={datePickerData.minDate}
                maxDate={datePickerData.maxDate}
                date={datePickerData.date}
                startDate={datePickerData.startDate}
                endDate={datePickerData.endDate}
                toggleOverlay={datePickerData.toggleOverlay}
                onDateSelected={datePickerData.onDateSelected}
                resetSelection={datePickerData.resetSelection}
                {...overlayProps}
              />
            </Overlay.Container>
          </Portal>
        )}
      </>
    );
  }
);

DatePicker.displayName = 'DatePicker';

DatePicker.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Variant of the field could be icon or field */
  variant: PropTypes.oneOf(Object.values(DATE_PICKER_VARIANTS)),
  /** A selected date for a calendar without range */
  date: PropTypes.instanceOf(Date),
  /** A selected start date for a calendar with range data (should be used with range=true) */
  startDate: PropTypes.instanceOf(Date),
  /** A selected end date for a calendar with range data (should be used with range=true) */
  endDate: PropTypes.instanceOf(Date),
  /** The uncontrolled date for calendar with single date select */
  defaultDate: PropTypes.instanceOf(Date),
  /** The uncontrolled start date for calendar with date range select */
  defaultStartDate: PropTypes.instanceOf(Date),
  /** The uncontrolled end date for calendar with date range select */
  defaultEndDate: PropTypes.instanceOf(Date),
  /** Allow selection of dates after minDate, overrides `disablePastDates` */
  minDate: PropTypes.instanceOf(Date),
  /** Allow selection of dates before maxDate, overrides `disableFutureDates` */
  maxDate: PropTypes.instanceOf(Date),
  /** Whether we should disable dates before today */
  disablePastDates: PropTypes.bool,
  /** Whether we should disable dates after today */
  disableFutureDates: PropTypes.bool,
  /** Flag whether the date picker is for a date range of a single value */
  range: PropTypes.bool,
  /** A callbcack function called when the selected date(s) has been changed */
  onChange: PropTypes.func,
  /** A callbcack function called when the active months have changed (arrow click, month/year selection) */
  onMonthChange: PropTypes.func,
  /** Handler to manually check if date is selectable */
  isDateSelectable: PropTypes.func,
  /** The format to display the date with */
  format: PropTypes.oneOf(Object.values(VALID_DATE_FORMATS)),
  /** Whether picker should be open by default */
  defaultOpen: PropTypes.bool,
  /** Controlled picker open state */
  open: PropTypes.bool,
  /** Whether the we should display double date picker */
  double: PropTypes.bool,
  /** Whether the input should display in error state */
  error: PropTypes.bool,
  /** Disable all functionality */
  disabled: PropTypes.bool,
  /** First day of week to display */
  firstDayOfWeek: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6]),
  /** Fill the blanks with days from prev/next month */
  showOutsideDays: PropTypes.bool,
  /** Whether we should allow range start and end to be the same date */
  allowSameStartAndEndDate: PropTypes.bool,
  /** Passed to the field, placeholder to display when there is no value - when passed in range mode, it will affect both start and end dates */
  placeholder: PropTypes.string,
  /** Passed to the field, placeholder to display when there is no startDate. this prop overrides `placeholder` */
  startDatePlaceholder: PropTypes.string,
  /** Passed to the field, placeholder to display when there is no endDate. this prop overrides `placeholder` */
  endDatePlaceholder: PropTypes.string,
  /** Props passed to DatePickerField or Button (when variant is icon) */
  triggerProps: PropTypes.object,
  /** Props passed to DatePickerOverlay */
  overlayProps: PropTypes.object,
  /* Props passed to each calendar */
  calendarProps: PropTypes.object,
  /** Render custom footer, executed in DatePickerOverlay */
  renderFooter: PropTypes.func,
  /** Render custom footer actions, executed in DatePickerOverlay */
  renderFooterActions: PropTypes.func,
  /** Render custom footer, executed in DatePicker */
  renderTrigger: PropTypes.func,
  /** Determines if Day Cell should render an event dot */
  hasEventForDate: PropTypes.func,
  /** Tooltip content for Day Cell */
  tooltipContentForDate: PropTypes.func,
  /** How much years to display under and above current year, defaults to +-20 */
  yearsToDisplay: PropTypes.number,
  /** Get the container elemenet for the overlay element */
  container: PropTypes.any,
  /** A callback function called when DatePicker overlay is being hidden (closed) */
  onHide: PropTypes.func,
};

export default Object.assign(DatePicker, {
  VARIANTS: DATE_PICKER_VARIANTS,
  VALID_FORMATS: VALID_DATE_FORMATS,
  Footer,
  Trigger,
});
